import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Header = ({ 
  wins = 0, 
  losses = 0,
  ties = 0, 
  seasonWins = 0, 
  seasonLosses = 0,
  totalGames = 0,
  usedTickets = 0,
  totalTickets = 0,
  usedParking = 0,
  totalParking = 0,
  partnerStats = [], // Dynamic partner stats: [{id, name, tickets, parking, used, balance}]
  onDataRefresh,
  isMobileView = false,
  isTablet = false,
  onToggleMobileView,
  onPrintReport,
  games = [],
  ticketData = {},
  appSettings = null,
  currentUser = null,
  currentEvent = null,
  ipAddress = '',
  onLogout = null
}) => {
  const { isAuthenticated, login, logout, isEmulating, exitEmulation, getEmulatedUser } = useAuth();
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [userEmail, setUserEmail] = useState(currentUser?.email || '');
  const [userPassword, setUserPassword] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  
  // Check if this is a family/personal event (non-sports)
  const isFamilyEvent = currentEvent?.sport?.toLowerCase() === 'family';
  // Check if this is a concerts/theater event
  const isConcertsEvent = currentEvent?.sport?.toLowerCase() === 'concerts';
  // Check if this is any non-sports event (no scores needed)
  const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
  
  // Get emulated user info if in emulation mode
  const emulatedUser = isEmulating ? getEmulatedUser() : null;
  
  // Debug biometric prompt state changes
  useEffect(() => {
    console.log('showBiometricPrompt changed to:', showBiometricPrompt);
  }, [showBiometricPrompt]);

  // Update email when currentUser changes
  useEffect(() => {
    if (currentUser?.email) {
      setUserEmail(currentUser.email);
    }
  }, [currentUser]);

  const [error, setError] = useState('');
  const [updatingScores, setUpdatingScores] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricNeverAsk, setBiometricNeverAsk] = useState(false);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [showUserPassword, setShowUserPassword] = useState(false);

  // Fetch live NBA data
  const fetchLiveData = async () => {
    try {
      const response = await axios.get(`${API}/nba/live-data`);
      if (response.data.success) {
        setLiveData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching live NBA data:', error);
    }
  };

  // Update user profile (email/password)
  const handleUpdateProfile = async () => {
    if (!currentUser?.email) return;
    
    try {
      const updates = {};
      if (userEmail && userEmail !== currentUser.email) {
        updates.email = userEmail;
      }
      if (userPassword) {
        updates.password = userPassword;
      }
      
      if (Object.keys(updates).length === 0) {
        setProfileMessage('No changes to save');
        return;
      }
      
      await axios.put(`${API}/users/${currentUser.email}`, updates);
      setProfileMessage('Profile updated successfully!');
      setUserPassword('');
      
      // If email changed, update localStorage or state
      if (updates.email) {
        setProfileMessage('Profile updated! Please log in again with new email.');
      }
    } catch (err) {
      setProfileMessage('Error updating profile');
    }
  };

  // Fetch live data on mount and every 60 minutes
  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 60 * 60 * 1000); // 60 minutes
    return () => clearInterval(interval);
  }, []);

  // Calculate next home game
  const getNextHomeGame = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < games.length; i++) {
      // Parse date as local (not UTC) to avoid timezone offset issues
      const [year, month, day] = games[i].date.split('-').map(Number);
      const gameDate = new Date(year, month - 1, day);
      gameDate.setHours(0, 0, 0, 0);
      
      // Check if game hasn't been played (no scores) and is in the future
      const gameData = ticketData[i] || {};
      const hasScore = (gameData.mavs_score > 0 || gameData.opponent_score > 0);
      
      if (gameDate >= today && !hasScore) {
        const daysUntil = Math.ceil((gameDate - today) / (1000 * 60 * 60 * 24));
        return {
          opponent: games[i].opponent,
          date: games[i].date,
          daysUntil: daysUntil
        };
      }
    }
    return null;
  };

  const nextGame = getNextHomeGame();

  // Biometric data is stored as JSON in 'biometric_data' key
  // Format: { [email]: { enabled: true, credentialId: '...', rawId: '...', eventId: '...' } }
  
  const getBiometricData = () => {
    try {
      const data = localStorage.getItem('biometric_data');
      return data ? JSON.parse(data) : {};
    } catch (e) {
      return {};
    }
  };
  
  const saveBiometricData = (email, userData) => {
    try {
      const data = getBiometricData();
      data[email] = { ...data[email], ...userData };
      localStorage.setItem('biometric_data', JSON.stringify(data));
      localStorage.setItem('last_biometric_email', email);
      console.log('Saved biometric data for:', email, userData);
    } catch (e) {
      console.error('Error saving biometric data:', e);
    }
  };

  // Check biometric status from localStorage on mount
  useEffect(() => {
    if (!currentUser?.email) return;
    
    try {
      const data = getBiometricData();
      const userData = data[currentUser.email] || {};
      
      console.log('Biometric check for user:', currentUser.email, userData);
      
      setBiometricEnabled(userData.enabled === true);
      setBiometricNeverAsk(userData.neverAsk === true);
      
      // Also save this as last user for login page
      localStorage.setItem('last_biometric_email', currentUser.email);
    } catch (storageError) {
      console.log('localStorage error:', storageError);
    }
  }, [currentUser?.email]);
  
  // Biometric prompt is now handled in CoverPage after login
  // Keep this disabled in Header
  
  const homeTotal = wins + losses + ties;
  const homeWinPct = homeTotal > 0 ? ((wins / homeTotal) * 100).toFixed(1) : '0.0';
  const homeRecordLabel = ties > 0 ? `${wins}-${losses}-${ties}` : `${wins}-${losses}`;
  
  const seasonTotal = seasonWins + seasonLosses;
  const seasonWinPct = seasonTotal > 0 ? ((seasonWins / seasonTotal) * 100).toFixed(1) : '0.0';
  const seasonRecordLabel = (seasonWins > 0 || seasonLosses > 0) ? `${seasonWins}-${seasonLosses}` : homeRecordLabel;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount));
  };

  // Get balance label (Over/Under)
  const getBalanceLabel = (balance) => {
    if (balance < 0) return 'Under';
    if (balance > 0) return 'Over';
    return 'Even';
  };

  const setupBiometric = async () => {
    try {
      if (typeof localStorage === 'undefined') {
        alert('Storage not available. Cannot enable fingerprint.');
        return;
      }

      if (!currentUser?.email) {
        alert('User not logged in. Please log in first.');
        return;
      }

      console.log('Starting biometric setup for:', currentUser.email);

      // Generate a challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challenge,
          rp: {
            name: "Mavs Tickets",
            id: window.location.hostname,
          },
          user: {
            id: new Uint8Array(16),
            name: currentUser.email,
            displayName: currentUser.name || "Ticket User",
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },
            { alg: -257, type: "public-key" }
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required",
          },
          timeout: 60000,
          attestation: "none"
        }
      });

      console.log('Credential created:', credential);

      if (credential) {
        try {
          const credId = credential.id;
          const rawIdBase64 = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
          
          // Save using JSON approach
          saveBiometricData(currentUser.email, {
            enabled: true,
            credentialId: credId,
            rawId: rawIdBase64,
            eventId: currentEvent?.id || '',
            setupDate: new Date().toISOString()
          });
          
          // Verify it was saved
          const data = getBiometricData();
          const saved = data[currentUser.email];
          console.log('Verified save:', saved);
          
          setBiometricEnabled(true);
          setShowBiometricPrompt(false);
          
          alert(`✅ Fingerprint enabled for ${currentUser.email}!\n\nYou can now use fingerprint to login.`);
        } catch (storageError) {
          console.error('localStorage error:', storageError);
          alert('Could not save fingerprint settings: ' + storageError.message);
        }
      } else {
        console.log('No credential returned');
        alert('Fingerprint setup was cancelled or failed.');
      }
    } catch (err) {
      console.error('Biometric setup error:', err);
      alert('Could not set up fingerprint: ' + err.message);
    }
  };

  const handleBiometricLogin = async () => {
    try {
      setError('');
      
      // Check if localStorage is available
      if (typeof localStorage === 'undefined') {
        setError('Storage not available');
        return;
      }

      const credentialId = localStorage.getItem('biometric_credential_id');
      const rawIdBase64 = localStorage.getItem('biometric_raw_id');
      
      if (!credentialId || !rawIdBase64) {
        setError('Biometric not set up');
        return;
      }

      // Generate a challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Decode the raw ID
      const rawId = Uint8Array.from(atob(rawIdBase64), c => c.charCodeAt(0));

      // Request authentication
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          allowCredentials: [{
            id: rawId,
            type: 'public-key',
            transports: ['internal']
          }],
          timeout: 60000,
          userVerification: "required"
        }
      });

      if (assertion) {
        // Biometric authentication successful
        login();
        
        // Refresh data after successful login
        if (onDataRefresh) {
          onDataRefresh();
        }
      }
    } catch (err) {
      console.error('Biometric login error:', err);
      alert('Fingerprint authentication failed. Please log in with email/password.');
    }
  };

  const handleLogout = () => {
    // Store that we want to switch events (soft logout)
    sessionStorage.setItem('switchingEvents', 'true');
    
    // Check if we're in emulation mode - preserve emulation state
    const isEmulatingMode = localStorage.getItem('isEmulating') === 'true';
    
    if (isEmulatingMode) {
      // For emulated users, just go back to event selection without clearing emulation
      // Store the emulated user info in session storage for CoverPage to pick up
      const emulatedUser = {
        email: localStorage.getItem('emulatedUserEmail'),
        name: localStorage.getItem('emulatedUserName'),
        partner_id: localStorage.getItem('emulatedUserPartnerId'),
        is_event_manager: localStorage.getItem('emulatedUserIsEventManager') === 'true'
      };
      sessionStorage.setItem('loggedInUser', JSON.stringify(emulatedUser));
      
      // Clear current event but keep emulation
      localStorage.removeItem('currentEvent');
      localStorage.setItem('isAuthenticated', 'true');
      
      // Call parent logout handler to reset cover page
      if (onLogout) {
        onLogout();
      }
    } else {
      // Normal logout flow
      logout();
      // Call parent logout handler to reset cover page
      if (onLogout) {
        onLogout();
      }
    }
  };

  const handleUpdateScores = async () => {
    try {
      setUpdatingScores(true);
      
      // Determine which API to call based on sport type
      const sport = currentEvent?.sport?.toLowerCase();
      const eventId = currentEvent?.id || 'mavericks-2025';
      
      let response;
      if (sport === 'hockey') {
        // NHL API for hockey teams
        response = await axios.post(`${API}/auth/update-nhl-scores?event_id=${eventId}`);
      } else if (sport === 'football') {
        // NFL API for football teams
        response = await axios.post(`${API}/auth/update-nfl-scores?event_id=${eventId}`);
      } else {
        // NBA API for basketball teams (default)
        response = await axios.post(`${API}/auth/update-scores?event_id=${eventId}`);
      }
      
      console.log('Scores updated:', response.data);
      
      // Refresh data after updating scores
      if (onDataRefresh) {
        onDataRefresh();
      }
      
      if (response.data.success) {
        alert(`Scores updated! Season: ${response.data.season_record || 'N/A'}${response.data.home_record ? `, Home: ${response.data.home_record}` : ''}`);
      } else {
        alert(`Update failed: ${response.data.error || response.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating scores:', err);
      alert('Error updating scores. Please try again.');
    } finally {
      setUpdatingScores(false);
    }
  };

  const handleClearBiometrics = () => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('biometric_enabled');
        localStorage.removeItem('biometric_credential_id');
        localStorage.removeItem('biometric_raw_id');
        localStorage.removeItem('biometric_user_email');
        localStorage.removeItem('biometric_event_id');
        localStorage.removeItem('biometric_never_ask');
        setBiometricEnabled(false);
        setBiometricNeverAsk(false);
        
        // Close any open menus/modals
        setShowResetMenu(false);
        setShowUserProfile(false);
        
        // Show prompt after a short delay on mobile
        setTimeout(() => {
          if (isMobileView) {
            setShowBiometricPrompt(true);
          } else {
            alert('Biometric data cleared. You can set it up again on a mobile device.');
          }
        }, 300);
      } else {
        alert('Storage not available.');
      }
    } catch (err) {
      console.error('Error clearing biometrics:', err);
      alert('Error clearing biometric data.');
    }
  };

  return (
    <>
      {/* Emulation Mode Banner */}
      {isEmulating && emulatedUser && (
        <div
          className="sticky top-0 z-[100] w-full bg-orange-500 text-white px-4"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
            paddingBottom: '0.5rem',
          }}
        >
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">👤</span>
              <span className="font-semibold">
                Emulating: {emulatedUser.name} ({emulatedUser.email})
              </span>
              {emulatedUser.partner_id && (
                <Badge variant="outline" className="bg-white/20 text-white border-white/40">
                  Partner {emulatedUser.partner_id}
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exitEmulation}
              className="bg-white text-orange-600 hover:bg-orange-100 border-white"
            >
              ✕ Exit Emulation
            </Button>
          </div>
        </div>
      )}
      
      <header
        className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        style={{
          // Respect iOS status-bar / notch so the sticky menu doesn't overlap
          // the phone info line (time / battery / signal). Only applied when
          // the emulation banner is NOT covering the top — that banner already
          // takes care of the inset for its own row.
          paddingTop: isEmulating && emulatedUser ? undefined : 'env(safe-area-inset-top, 0px)',
        }}
      >
        <div className={`${isMobileView ? 'px-3 py-2.5' : 'container mx-auto px-4 py-3'}`}>
          {/* Mobile: Single Row Header */}
          {isMobileView ? (
            <div className="flex items-center justify-between">
              {/* Left - Logo and compact info */}
              <div className="flex items-center gap-2">
                {/* Dynamic Team Logo */}
                {currentEvent?.sport?.toLowerCase() === 'hockey' ? (
                  <img src="/dallas-stars-logo.svg" alt="Dallas Stars" className="w-9 h-9" />
                ) : currentEvent?.sport?.toLowerCase() === 'football' ? (
                  <img src="/dallas-cowboys-logo.png" alt="Dallas Cowboys" className="w-9 h-9 object-contain" />
                ) : currentEvent?.sport?.toLowerCase() === 'basketball' ? (
                  <img src="/dallas-mavs-logo.png" alt="Dallas Mavericks" className="w-9 h-9 object-contain" />
                ) : currentEvent?.sport?.toLowerCase() === 'baseball' ? (
                  <img src="/texas-rangers-logo.png" alt="Texas Rangers" className="w-9 h-9 object-contain" />
                ) : (
                  <img src="/favicon-ticketasaurus.svg" alt="Team Logo" className="w-9 h-9" />
                )}
                {/* Records - Hide for non-sports events */}
                {!isNonSportsEvent && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm bg-muted/50 rounded px-2 py-1 font-bold">
                      {seasonRecordLabel}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      🏠{homeRecordLabel}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Right - Actions */}
              <div className="flex items-center gap-1.5">
                {isAuthenticated && (
                  <>
                    {!isNonSportsEvent && (
                      <Button variant="ghost" size="sm" onClick={handleUpdateScores} disabled={updatingScores} className="h-9 w-9 p-0 text-lg">
                        {updatingScores ? '⏳' : '🔄'}
                      </Button>
                    )}
                    <Button data-testid="print-report-btn-mobile" variant="ghost" size="sm" onClick={onPrintReport} className="h-9 w-9 p-0 text-lg">
                      📄
                    </Button>
                    {currentUser && (
                      <Badge variant="outline" className="text-sm px-2 py-1 font-bold">
                        {currentUser.partner_id || currentUser.name?.charAt(0)}
                      </Badge>
                    )}
                  </>
                )}
                {/* Tablet View Toggle - Switch between mobile and desktop views */}
                {isTablet && onToggleMobileView && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onToggleMobileView} 
                    className="h-9 w-9 p-0 text-lg"
                    title={isMobileView ? 'Switch to Desktop View' : 'Switch to Mobile View'}
                  >
                    {isMobileView ? '🖥️' : '📱'}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleLogout} className="h-9 w-9 p-0 text-lg">
                  ↩️
                </Button>
              </div>
            </div>
          ) : (
            /* Desktop: Original two-row layout */
            <>
              <div className="flex items-center justify-between mb-2">
                {/* Left - Logo and Title */}
                <div className="flex items-center gap-2">
                  {/* Dynamic Team Logo */}
                  {currentEvent?.sport?.toLowerCase() === 'hockey' ? (
                    <img src="/dallas-stars-logo.svg" alt="Dallas Stars" className="w-10 h-10" />
                  ) : currentEvent?.sport?.toLowerCase() === 'football' ? (
                    <img src="/dallas-cowboys-logo.png" alt="Dallas Cowboys" className="w-10 h-10 object-contain" />
                  ) : currentEvent?.sport?.toLowerCase() === 'basketball' ? (
                    <img src="/dallas-mavs-logo.png" alt="Dallas Mavericks" className="w-10 h-10 object-contain" />
                  ) : currentEvent?.sport?.toLowerCase() === 'baseball' ? (
                    <img src="/texas-rangers-logo.png" alt="Texas Rangers" className="w-10 h-10 object-contain" />
                  ) : (
                    <img src="/favicon-ticketasaurus.svg" alt="Team Logo" className="w-10 h-10" />
                  )}
                  <div>
                    <h1 className="text-xl font-bold font-display text-gradient whitespace-nowrap">
                      {currentEvent?.name 
                        ? `${currentEvent.name} - Home Game Ticket Scorecard - ${currentEvent.season || '2025-2026'} Season`
                        : (appSettings?.app_title || "Home Game Ticket Scorecard - 2025-2026 Season")}
                    </h1>
                  </div>
                </div>
                
                {/* Right - Games Badge */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const regularGames = games?.filter(g => g.season_type !== 'playoff' && g.type !== 'playoff').length || 0;
                    const playoffGames = games?.filter(g => g.season_type === 'playoff' || g.type === 'playoff').length || 0;
                    
                    return (
                      <>
                        <Badge variant="default" className="text-sm px-3 py-1.5">
                          📅 {regularGames} Reg
                        </Badge>
                        {playoffGames > 0 && (
                          <Badge variant="destructive" className="text-sm px-3 py-1.5">
                            🏆 {playoffGames} PO
                          </Badge>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}

          {/* Mobile: Partner Stats - Removed for cleaner mobile view, available elsewhere */}

          {/* News Ticker - Only show on Desktop */}
          {!isMobileView && appSettings?.ticker_enabled !== false && (() => {
            const eventName = currentEvent?.name || 'Dallas Mavericks';
            const sport = currentEvent?.sport?.toLowerCase();
            const sportEmoji = sport === 'hockey' ? '🏒' : sport === 'football' ? '🏈' : sport === 'baseball' ? '⚾' : sport === 'family' ? '🏠' : sport === 'concerts' ? '🎭' : '🏀';
            const isMavericks = currentEvent?.id === 'mavericks-2025' || !currentEvent;
            const isFamilyEvent = sport === 'family';
            const isConcertsEvent = sport === 'concerts';
            const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
            
            // Build ticker items - simplified for non-sports events
            const tickerItems = isFamilyEvent ? [
              `🏠 ${eventName} • Family Calendar`,
              `📅 ${totalGames} events scheduled`,
              `🗓️ ${appSettings?.season_year || currentEvent?.season || '2026'} Calendar`
            ] : isConcertsEvent ? [
              `🎭 ${eventName} • Entertainment Calendar`,
              `🎫 ${totalGames} shows scheduled`,
              `🎪 ${appSettings?.season_year || currentEvent?.season || '2026'} Season`,
              totalGames > 0 ? `🎟️ Tickets: ${usedTickets}/${totalTickets} assigned` : null
            ].filter(Boolean) : [
              `${sportEmoji} Season Record: ${(seasonWins > 0 || seasonLosses > 0) ? seasonWins : wins}-${(seasonWins > 0 || seasonLosses > 0) ? seasonLosses : losses} (${(seasonWins > 0 || seasonLosses > 0) ? seasonWinPct : homeWinPct}%) • Home Record: ${wins}-${losses} (${homeWinPct}%)`,
              appSettings?.show_next_game !== false && nextGame ? `🗓️ Next Home Game: vs ${nextGame.opponent} in ${nextGame.daysUntil} ${nextGame.daysUntil === 1 ? 'day' : 'days'}` : null,
              // Only show NBA-specific data for Mavericks
              isMavericks && appSettings?.show_standings !== false && liveData ? `📊 ${liveData.conference_standing}` : (!isMavericks && totalGames > 0 ? `📊 ${eventName} Home Games` : null),
              isMavericks && appSettings?.show_player_stats !== false && liveData && liveData.player_stats.length > 0 ? `⭐ Leading Scorer: ${liveData.player_stats[0].name} ${liveData.player_stats[0].ppg} PPG` : null,
              isMavericks && appSettings?.show_recent_games !== false && liveData && liveData.recent_results.length > 0 ? `📈 Last 3 Games: ${liveData.recent_results.join(' • ')}` : null,
              totalGames > 0 ? `🎯 Season Progress: ${wins + losses} of ${totalGames} home games played` : `🎯 No games scheduled yet`,
              `🏆 ${appSettings?.season_year || currentEvent?.season || '2025-26'} Season • ${eventName}`,
              totalGames === 0 ? `📅 Add games via Event Admin` : `⭐ Home Games`
            ].filter(Boolean);
            
            const tickerSpeed = appSettings?.ticker_speed || 40;
            
            return (
              <div className="relative overflow-hidden bg-primary/10 rounded-md py-1.5 mb-2 border border-primary/20">
                <div className="ticker-wrapper">
                  <div className="ticker-content" style={{ animationDuration: `${tickerSpeed}s` }}>
                    {tickerItems.map((item, idx) => (
                      <span key={`ticker-1-${idx}`} className="ticker-item">{item}</span>
                    ))}
                    {tickerItems.map((item, idx) => (
                      <span key={`ticker-2-${idx}`} className="ticker-item">{item}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Second Row - Buttons and Records - Desktop Only */}
          {!isMobileView && (
          <div className="flex items-center justify-end mb-3">
            {/* Buttons and Records */}
            <div className="flex items-center gap-3">
              {/* Settings Icon - Only show for event managers or superadmins */}
              {(currentUser?.is_event_manager || currentUser?.role === 'superadmin') && (
              <div className="relative">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowResetMenu(!showResetMenu)}
                  className="h-8 w-8 p-0"
                  title="Event Admin Settings"
                >
                  ⚙️
                </Button>
                
                {/* Settings Dropdown Menu */}
                {showResetMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      {/* Event Admin - Only show on desktop and for event managers or superadmins */}
                      {isAuthenticated && !isMobileView && (currentUser?.is_event_manager || currentUser?.role === 'superadmin') && (
                        <a
                          href="/event-admin"
                          className="block w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm font-semibold text-gray-900"
                          onClick={() => setShowResetMenu(false)}
                        >
                          ⚙️ Event Admin
                        </a>
                      )}
                      <button
                        onClick={() => setShowResetMenu(false)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm text-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}
              
              
              {/* Action Buttons */}
              {isAuthenticated && (
                <div className="flex items-center gap-2">
                  {!isNonSportsEvent && (
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={handleUpdateScores}
                      disabled={updatingScores}
                      className="h-8 text-xs"
                      title="Update scores from API"
                    >
                      {updatingScores ? '⏳' : '🔄'} Scores
                    </Button>
                  )}
                  <Button 
                    data-testid="print-report-btn"
                    variant="default" 
                    size="sm"
                    onClick={onPrintReport}
                    className="h-8 text-xs"
                    title="Print detailed report"
                  >
                    🖨️ Report
                  </Button>
                  {/* Tablet View Toggle - Switch back to mobile view */}
                  {isTablet && onToggleMobileView && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={onToggleMobileView}
                      className="h-8 text-xs"
                      title={isMobileView ? 'Switch to Desktop View' : 'Switch to Mobile View'}
                    >
                      {isMobileView ? '🖥️ Desktop' : '📱 Mobile'}
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleLogout}
                    className="h-8"
                  >
                    ↩️ Switch Event
                  </Button>
                </div>
              )}
              
              {/* Season Record - Compact - Hide for non-sports events */}
              {!isNonSportsEvent && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card border border-border">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Season</p>
                    <p className="text-sm font-bold font-display text-foreground">
                      {/* Use seasonWins/Losses if available, otherwise fall back to home record */}
                      {seasonRecordLabel}
                    </p>
                  </div>
                  <div className="w-px h-8 bg-border"></div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Home</p>
                    <p className="text-sm font-bold font-display text-foreground">{homeRecordLabel}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Bottom Row - Statistics - Desktop Only - Hide sports stats for non-sports events */}
          {!isMobileView && isAuthenticated && !isNonSportsEvent && (
            /* Desktop Stats - Horizontal Layout */
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-5">
                <div>
                  <span className="text-muted-foreground">Games: </span>
                  <span className="font-semibold">{totalGames}</span>
                </div>
                <div className="w-px h-4 bg-border"></div>
                <div>
                  <span className="text-muted-foreground">Tickets: </span>
                  <span className="font-semibold">{usedTickets}/{totalTickets}</span>
                  <span className="text-muted-foreground ml-1">({((usedTickets/totalTickets)*100).toFixed(0)}%)</span>
                </div>
                <div className="w-px h-4 bg-border"></div>
                <div>
                  <span className="text-muted-foreground">Parking: </span>
                  <span className="font-semibold">{usedParking}/{totalParking}</span>
                  <span className="text-muted-foreground ml-1">({((usedParking/totalParking)*100).toFixed(0)}%)</span>
                </div>
              </div>
              
              <div className="flex items-center gap-5">
                {partnerStats.map((partner, idx) => (
                  <React.Fragment key={partner.id}>
                    {idx > 0 && <div className="w-px h-4 bg-border"></div>}
                    <div>
                      <span className={`font-semibold ${idx === 0 ? 'text-primary' : ''}`} style={idx > 0 ? {color: ['#f59e0b', '#10b981', '#8b5cf6', '#ec4899'][idx % 4]} : {}}>
                        {partner.id}: 
                      </span>
                      <span className="text-muted-foreground">{partner.tickets} tix, {partner.parking} park</span>
                      <span className="text-muted-foreground ml-2">•</span>
                      <span className="text-muted-foreground ml-2">{formatCurrency(partner.used)} used, {formatCurrency(partner.balance)} {getBalanceLabel(partner.balance)}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          
          {/* Stats row for concerts/theater events - shows tickets/parking but not game stats */}
          {!isMobileView && isAuthenticated && isConcertsEvent && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-5">
                <div>
                  <span className="text-muted-foreground">Shows: </span>
                  <span className="font-semibold">{totalGames}</span>
                </div>
                <div className="w-px h-4 bg-border"></div>
                <div>
                  <span className="text-muted-foreground">Tickets: </span>
                  <span className="font-semibold">{usedTickets}/{totalTickets}</span>
                  <span className="text-muted-foreground ml-1">({((usedTickets/totalTickets)*100).toFixed(0)}%)</span>
                </div>
                <div className="w-px h-4 bg-border"></div>
                <div>
                  <span className="text-muted-foreground">Parking: </span>
                  <span className="font-semibold">{usedParking}/{totalParking}</span>
                  <span className="text-muted-foreground ml-1">({((usedParking/totalParking)*100).toFixed(0)}%)</span>
                </div>
              </div>
              
              <div className="flex items-center gap-5">
                {partnerStats.map((partner, idx) => (
                  <React.Fragment key={partner.id}>
                    {idx > 0 && <div className="w-px h-4 bg-border"></div>}
                    <div>
                      <span className={`font-semibold ${idx === 0 ? 'text-primary' : ''}`} style={idx > 0 ? {color: ['#f59e0b', '#10b981', '#8b5cf6', '#ec4899'][idx % 4]} : {}}>
                        {partner.id}: 
                      </span>
                      <span className="text-muted-foreground">{partner.tickets} tix, {partner.parking} park</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          
          {/* Simple stats row for family events */}
          {!isMobileView && isAuthenticated && isFamilyEvent && (
            <div className="flex items-center text-sm">
              <div>
                <span className="text-muted-foreground">Total Events: </span>
                <span className="font-semibold">{totalGames}</span>
              </div>
            </div>
          )}
        </div>
      </header>
      
      {/* Biometric prompt is now in CoverPage - removed from Header */}

      {/* User Profile Dialog */}
      <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
        <DialogContent className="w-[90vw] max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl">User Profile</DialogTitle>
            <DialogDescription>
              View and update your account settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Info Display */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Logged in as:</span>
                <span className="font-bold">{currentUser?.name || 'Unknown'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Partner ID:</span>
                <span className="font-mono font-bold">{currentUser?.partner_id || 'N/A'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Your IP:</span>
                <span className="font-mono text-sm">{ipAddress || 'Unknown'}</span>
              </div>
            </div>

            {/* Update Email */}
            <div>
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>

            {/* Update Password */}
            <div>
              <label className="text-sm font-medium">New Password</label>
              <div className="relative mt-1">
                <Input
                  type={showUserPassword ? "text" : "password"}
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowUserPassword(!showUserPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showUserPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Security Actions */}
            <div className="pt-2 border-t border-border space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Security</p>
              
              {/* Show Enable Biometric button if not enabled and on mobile */}
              {!biometricEnabled && !biometricNeverAsk && (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => {
                    setShowUserProfile(false);
                    setTimeout(() => setShowBiometricPrompt(true), 200);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  🔐 Enable Quick Login
                </Button>
              )}
              
              {/* Show status if biometric is enabled */}
              {biometricEnabled && (
                <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-sm text-green-700">✓ Quick Login Enabled</span>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Reset biometric settings? You can set up quick login again.')) {
                      const email = currentUser?.email;
                      
                      // Clear from JSON store
                      if (email) {
                        const data = getBiometricData();
                        delete data[email];
                        localStorage.setItem('biometric_data', JSON.stringify(data));
                      }
                      
                      // Also clear all legacy keys
                      localStorage.removeItem('biometric_enabled');
                      localStorage.removeItem('biometric_credential_id');
                      localStorage.removeItem('biometric_raw_id');
                      localStorage.removeItem('biometric_user_email');
                      localStorage.removeItem('biometric_event_id');
                      localStorage.removeItem('biometric_never_ask');
                      
                      // Clear user-specific legacy keys too
                      if (email) {
                        localStorage.removeItem(`biometric_${email}_enabled`);
                        localStorage.removeItem(`biometric_${email}_credential_id`);
                        localStorage.removeItem(`biometric_${email}_raw_id`);
                        localStorage.removeItem(`biometric_${email}_event_id`);
                        localStorage.removeItem(`biometric_${email}_never_ask`);
                      }
                      
                      setBiometricEnabled(false);
                      setBiometricNeverAsk(false);
                      setShowUserProfile(false);
                      
                      // Show prompt immediately
                      setTimeout(() => {
                        setShowBiometricPrompt(true);
                      }, 300);
                      
                      alert('✅ Biometric reset! Setup prompt will appear now.');
                    }
                  }}
                  className="flex-1"
                >
                  🔄 Reset Biometric
                </Button>
              </div>
            </div>

            {profileMessage && (
              <p className={`text-sm text-center ${profileMessage.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>
                {profileMessage}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUserProfile(false);
              setProfileMessage('');
              setUserPassword('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProfile}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Biometric Setup Dialog */}
      <Dialog open={showBiometricPrompt} onOpenChange={setShowBiometricPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">
              <span className="text-4xl block mb-2">🔐</span>
              Enable Quick Login?
            </DialogTitle>
            <DialogDescription className="text-center">
              Use Face ID, Touch ID, or fingerprint to quickly log in next time without entering your password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <Button 
              onClick={async () => {
                await setupBiometric();
                setShowBiometricPrompt(false);
              }}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              🔐 Yes, Enable Quick Login
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                // Maybe later - increment skip count
                if (currentUser?.email) {
                  const data = getBiometricData();
                  const userData = data[currentUser.email.toLowerCase()] || {};
                  const skipCount = (userData.skipCount || 0) + 1;
                  saveBiometricData(currentUser.email.toLowerCase(), { 
                    ...userData,
                    skipCount,
                    lastSkipped: new Date().toISOString()
                  });
                }
                setShowBiometricPrompt(false);
              }}
              className="w-full"
            >
              Maybe Later
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                // Never ask again
                if (currentUser?.email) {
                  saveBiometricData(currentUser.email.toLowerCase(), { neverAsk: true });
                  setBiometricNeverAsk(true);
                }
                setShowBiometricPrompt(false);
              }}
              className="w-full text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              ✕ Never Ask Again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
